import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  ExternalLink, 
  RefreshCw,
  Unplug,
  CreditCard
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import mollieLogo from "@/assets/mollie-logo.png";

interface MollieConnectionCardProps {
  ptaId: string | null;
  onConnectionChange?: () => void;
}

interface MollieAccount {
  id: string;
  pta_id: string;
  mollie_organization_id: string | null;
  mollie_profile_id: string | null;
  onboarding_status: string;
  can_receive_payments: boolean;
  connected_at: string;
  updated_at: string;
}

export function MollieConnectionCard({ ptaId, onConnectionChange }: MollieConnectionCardProps) {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [mollieAccount, setMollieAccount] = useState<MollieAccount | null>(null);

  useEffect(() => {
    if (ptaId) {
      fetchMollieAccount();
    }
  }, [ptaId]);

  const fetchMollieAccount = async () => {
    if (!ptaId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .rpc('get_pta_mollie_account_safe', { p_pta_id: ptaId })
        .maybeSingle();

      if (error) {
        console.error('Error fetching Mollie account:', error);
      } else {
        setMollieAccount(data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!ptaId) return;
    
    setConnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('mollie-oauth-start', {
        body: { ptaId },
      });

      if (error) throw error;

      if (data?.authUrl) {
        window.location.href = data.authUrl;
      } else {
        throw new Error('No authorization URL returned');
      }
    } catch (err: any) {
      toast({
        title: "Connection Failed",
        description: err.message || "Failed to start Mollie connection",
        variant: "destructive",
      });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!ptaId) return;
    
    setDisconnecting(true);
    try {
      const { error } = await supabase
        .from('pta_mollie_accounts')
        .delete()
        .eq('pta_id', ptaId);

      if (error) throw error;

      setMollieAccount(null);
      toast({
        title: "Mollie Disconnected",
        description: "Your Mollie account has been disconnected",
      });
      onConnectionChange?.();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to disconnect Mollie",
        variant: "destructive",
      });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleRefreshStatus = async () => {
    setRefreshing(true);
    await fetchMollieAccount();
    setRefreshing(false);
    toast({
      title: "Status Refreshed",
      description: "Mollie account status has been updated",
    });
  };

  // Check if the account is actually connected (has tokens/organization ID from completed OAuth)
  const isActuallyConnected = (account: MollieAccount | null): boolean => {
    if (!account) return false;
    // Only consider connected if we have an organization ID (set after OAuth callback completes)
    return !!account.mollie_organization_id;
  };

  const getStatusBadge = () => {
    if (!mollieAccount) return null;

    if (mollieAccount.can_receive_payments) {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Active
        </Badge>
      );
    }

    switch (mollieAccount.onboarding_status) {
      case 'completed':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Verified
          </Badge>
        );
      case 'in-review':
        return (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            In Review
          </Badge>
        );
      case 'needs-data':
      default:
        return (
          <Badge className="bg-orange-100 text-orange-800 border-orange-200">
            <AlertCircle className="h-3 w-3 mr-1" />
            Needs Setup
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Mollie Payments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Mollie Payments
            </CardTitle>
            <CardDescription>
              Accept payments via iDEAL, credit cards, and more European payment methods
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {mollieAccount && isActuallyConnected(mollieAccount) && getStatusBadge()}
            <img src={mollieLogo} alt="Mollie" className="h-6 w-auto" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {mollieAccount && isActuallyConnected(mollieAccount) ? (
          <div className="space-y-4">
            {/* Connection Status */}
            <div className="grid gap-3">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-muted-foreground">Organization ID</span>
                <span className="text-sm font-mono">
                  {mollieAccount.mollie_organization_id 
                    ? `${mollieAccount.mollie_organization_id.slice(0, 8)}...` 
                    : 'Not set'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-muted-foreground">Profile ID</span>
                <span className="text-sm font-mono">
                  {mollieAccount.mollie_profile_id 
                    ? `${mollieAccount.mollie_profile_id.slice(0, 8)}...` 
                    : 'Not set'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-muted-foreground">Connected</span>
                <span className="text-sm">
                  {new Date(mollieAccount.connected_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-muted-foreground">Can Receive Payments</span>
                <span className="text-sm">
                  {mollieAccount.can_receive_payments ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                  )}
                </span>
              </div>
            </div>

            {/* Onboarding Alert */}
            {!mollieAccount.can_receive_payments && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {mollieAccount.onboarding_status === 'needs-data' ? (
                    <>
                      Complete your Mollie onboarding to start accepting payments.{' '}
                      <a 
                        href="https://my.mollie.com/dashboard" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        Go to Mollie Dashboard
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </>
                  ) : (
                    'Your account is being reviewed by Mollie. This usually takes 1-2 business days.'
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Payment Methods Info */}
            {mollieAccount.can_receive_payments && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-medium mb-2">
                  ✓ Ready to accept payments
                </p>
                <p className="text-xs text-green-700">
                  Parents can now pay using iDEAL, credit cards, Bancontact, and other Mollie-supported methods.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefreshStatus}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh Status
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('https://my.mollie.com/dashboard', '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Mollie Dashboard
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                <Unplug className="h-4 w-4 mr-2" />
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Benefits */}
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium mb-2">Why connect Mollie?</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Accept iDEAL, credit cards, Bancontact, and more</li>
                <li>• Lower transaction fees for European payments</li>
                <li>• Funds paid directly to your PTA account</li>
                <li>• Popular choice for UK and EU-based PTAs</li>
              </ul>
            </div>

            {/* Connect Button */}
            <Button 
              onClick={handleConnect} 
              disabled={connecting}
              className="w-full"
            >
              {connecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Connect Mollie Account
                </>
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              You'll be redirected to Mollie to authorize the connection
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
